import FeatureGates, { type GetExperimentValueOptions } from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';

import type { ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';
import { isEnglishLocale } from '../locale';
import { GhostTextExperimentCohort } from '../quick-find/ghost-text';

export const serveGithubLinksHydrationWithoutExposure: () => boolean = (): boolean => {
	const cohort = FeatureGates.getExperimentValue<string>(
		'enable_github_links_hydration_in_quickfind_recent',
		'cohort',
		'control',
		{ fireExperimentExposure: false },
	);

	return cohort === 'test';
};

export const serve3PGhostTextExperiment: ({
	thirdPartyConfigs,
	locale,
}: {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	locale: string;
}) => boolean = ({
	thirdPartyConfigs,
	locale,
}: {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	locale: string;
}): boolean => {
	if (!isEnglishLocale(locale) && !fg('confluence_quick_find_ghost_text_i18n')) {
		return false;
	}

	const isEligible3PUser = Object.keys(thirdPartyConfigs).some(
		(productKey) => thirdPartyConfigs[productKey]?.userNeedsOAuth === false,
	);

	if (!isEligible3PUser) {
		return false;
	}

	return (
		FeatureGates.getExperimentValue<string>(
			'confluence_quick_find_ghost_text_3p_experiment',
			'cohort',
			GhostTextExperimentCohort.CONTROL,
		) === GhostTextExperimentCohort.THREE_P
	);
};

const getAutoAppliedFiltersCohort = (
	options: GetExperimentValueOptions<string> = {
		fireExperimentExposure: false,
	},
) =>
	FeatureGates.getExperimentValue<string>(
		'fps_auto_applied_filters_experiment_v1',
		'cohort',
		'control',
		options,
	);

export const serveStickyAutoAppliedFilters: (
	options?: GetExperimentValueOptions<string>,
) => boolean = (options?: GetExperimentValueOptions<string>) =>
	getAutoAppliedFiltersCohort(options) === 'sticky';

export const serveNonStickyAutoAppliedFilters: (
	options?: GetExperimentValueOptions<string>,
) => boolean = (options?: GetExperimentValueOptions<string>) =>
	getAutoAppliedFiltersCohort(options) === 'non-sticky';

export const serveAutoAppliedFiltersExperiment: (
	options?: GetExperimentValueOptions<string>,
) => boolean = (options?: GetExperimentValueOptions<string>) => {
	const cohort = getAutoAppliedFiltersCohort(options);
	return cohort === 'sticky' || cohort === 'non-sticky';
};
